//
//  HelpTourStepAnchor.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module anchors the current tour step's popover over its target element.
//

import { OnboardingTour, type OnboardingTourController } from "@gfazioli/mantine-onboarding-tour";
import { Box, type PopoverProps } from "@mantine/core";
import { useEffect, useState, type FC } from "react";

import { findDisplayedTourTarget, findTourTarget, revealTourTarget } from "./HelpTourTarget";

/** How many frames to wait for a revealed target to be shown before giving up on it. */
const revealFrames = 60;

/** Supplies the running tour; the current step names the target to anchor to. */
export type HelpTourStepAnchorProps = {
    tourController: OnboardingTourController;
};

export type AnchorRect = { top: number; left: number; width: number; height: number };

/** The page content area, which popovers stay within: the viewport less the help aside. */
export type ContentBounds = { x: number; y: number; width: number; height: number };

/** Space kept between a popover and the edge of the content area. */
const edgePadding = 12;
/** Widest a popover may be. */
const popoverMaxWidth = 360;
/** A target taller than this share of the viewport has its popover placed just inside its top. */
const tallTargetShare = 0.45;

/** Measures the content area, excluding the help aside when it is open. */
const measureBounds = (): ContentBounds => {
    const aside = document.querySelector(".mantine-AppShell-aside");
    const asideLeft = aside?.getBoundingClientRect().left;
    const width =
        asideLeft !== undefined && asideLeft > 0 && asideLeft < window.innerWidth
            ? asideLeft
            : window.innerWidth;

    return { x: 0, y: 0, width, height: window.innerHeight };
};

/**
 * Builds the popover placement for a target.
 *
 * Postconditions: Popovers open below their target, flipping above and shifting sideways to stay
 * within the content area, and are capped at the space left there, scrolling when their content is
 * longer. A tall target's popover sits just inside its top edge instead, because there is no room
 * beside it.
 */
export const tourPopoverPlacement = (rect: AnchorRect, bounds: ContentBounds) => {
    const tall = rect.height > window.innerHeight * tallTargetShare;
    const middlewares: NonNullable<PopoverProps["middlewares"]> = {
        flip: tall ? false : { boundary: bounds, padding: edgePadding },
        shift: { boundary: bounds, padding: edgePadding },
        size: {
            boundary: bounds,
            padding: edgePadding,
            apply: ({ availableHeight, elements }) => {
                elements.floating.style.maxHeight = `${Math.max(availableHeight, 160)}px`;
                elements.floating.style.overflowY = "auto";
            }
        }
    };

    return {
        position: "bottom" as const,
        offset: tall ? edgePadding - rect.height : 8,
        width: Math.max(Math.min(popoverMaxWidth, bounds.width - edgePadding * 2), 200),
        middlewares,
        floatingStrategy: "fixed" as const,
        // A fade can be interrupted by a quick step change and leave the popover invisible.
        transitionProps: { duration: 0 },
        withinPortal: true
    };
};

/**
 * Measures the visible part of an element, so a tall target such as a grid keeps its popover on
 * screen.
 */
const measure = (element: Element): AnchorRect | undefined => {
    const rect = element.getBoundingClientRect();
    const top = Math.max(rect.top, 0);
    const left = Math.max(rect.left, 0);
    const bottom = Math.min(rect.bottom, window.innerHeight);
    const right = Math.min(rect.right, window.innerWidth);

    return bottom > top && right > left
        ? { top, left, width: right - left, height: bottom - top }
        : undefined;
};

/**
 * Presents the current step's popover over its target without touching the target itself.
 *
 * Postconditions: The target is scrolled to the centre of the viewport when its step starts, and a
 * fixed, pointer-transparent proxy follows its visible bounds through scrolling and resizing. The
 * library focus reveal and popover wrap the proxy, and the provider's overlay cuts out the proxy,
 * so the real element stays mounted and remains usable while highlighted. A target hidden in a
 * collapsed section is revealed first; one that has gone, or is not shown within a second, renders
 * nothing.
 */
const HelpTourStepAnchor: FC<HelpTourStepAnchorProps> = ({ tourController }) => {
    const targetId = tourController.selectedStepId;
    const [rect, setRect] = useState<AnchorRect>();
    const [bounds, setBounds] = useState<ContentBounds>();

    useEffect(() => {
        /** Follows an element's visible bounds until the returned cleanup is called. */
        const track = (element: Element) => {
            let frame = 0;
            const update = () => {
                cancelAnimationFrame(frame);
                frame = requestAnimationFrame(() => {
                    setRect(measure(element));
                    setBounds(measureBounds());
                });
            };
            const resizeObserver = new ResizeObserver(update);
            resizeObserver.observe(element);
            window.addEventListener("scroll", update, true);
            window.addEventListener("resize", update);
            update();

            return () => {
                cancelAnimationFrame(frame);
                resizeObserver.disconnect();
                window.removeEventListener("scroll", update, true);
                window.removeEventListener("resize", update);
            };
        };

        const hidden = targetId ? findTourTarget(targetId) : undefined;
        if (!targetId || !hidden) {
            setRect(undefined);
            return undefined;
        }

        let frame = 0;
        let cleanup: (() => void) | undefined;
        const follow = (element: Element) => {
            element.scrollIntoView({ block: "center", inline: "nearest" });
            cleanup = track(element);
        };

        const shown = findDisplayedTourTarget(targetId);
        if (shown) {
            follow(shown);
        } else {
            // Ask the owner of the collapsed section to open it, then wait for the target to show.
            setRect(undefined);
            revealTourTarget(hidden);
            let remaining = revealFrames;
            const poll = () => {
                const element = findDisplayedTourTarget(targetId);
                if (element) {
                    follow(element);
                } else if (--remaining > 0) {
                    frame = requestAnimationFrame(poll);
                }
            };
            frame = requestAnimationFrame(poll);
        }

        return () => {
            cancelAnimationFrame(frame);
            cleanup?.();
        };
    }, [targetId]);

    if (!rect || !bounds) {
        return null;
    }

    return (
        <OnboardingTour.FocusReveal
            key={targetId}
            focused
            withOverlay={false}
            withReveal={false}
            focusedMode="glow"
            popoverProps={tourPopoverPlacement(rect, bounds)}
            transitionProps={{ duration: 0, exitDuration: 0 }}
            popoverContent={
                <OnboardingTour.PopoverContent
                    tourController={tourController}
                    prevStepNavigation="Back"
                    nextStepNavigation="Next"
                    endStepNavigation="Done"
                    skipNavigation="Skip tour"
                />
            }
        >
            <Box
                aria-hidden
                style={{
                    position: "fixed",
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    pointerEvents: "none",
                    borderRadius: "var(--mantine-radius-sm)"
                }}
            />
        </OnboardingTour.FocusReveal>
    );
};

export default HelpTourStepAnchor;
